import { DEFAULT_WORKING_HOURS, WORKING_SHIFTS_MAX, type WorkingDay, type WorkingHours, type WorkingShifts } from '@clinic/contracts';
import { toPersianDigits } from '@clinic/money-date';

/** The grid the server accepts: quarter hours into the Tehran day (migration 0042). */
export const HOURS_STEP = 15;
export const DAY_END = 24 * 60;

/** Every grid time from `from` to `to` inclusive. */
export function timeOptions(from: number, to: number): number[] {
  const out: number[] = [];
  for (let m = from; m <= to; m += HOURS_STEP) out.push(m);
  return out;
}

/** 510 becomes «۰۸:۳۰» (Persian digits, like the rest of the site). */
export function formatClock(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return toPersianDigits(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
}

/** Whether an open day's hours are acceptable: the end is after the start, both on the grid. */
export function dayProblem(day: WorkingDay): 'endBeforeStart' | null {
  if (!day.open) return null;
  return day.from < day.to ? null : 'endBeforeStart';
}

/** The first problem of each day (null when it is fine). */
export function hoursProblems(hours: readonly WorkingDay[]): ('endBeforeStart' | null)[] {
  return hours.map(dayProblem);
}

/** Every day gets the hours of day `index`. */
export function copyToAll(hours: readonly WorkingDay[], index: number): WorkingDay[] {
  const source = hours[index];
  if (!source) return hours.map((d) => ({ ...d }));
  return hours.map(() => ({ ...source }));
}

/** A day changed; closed days keep their hours so opening it again restores them. */
export function withDay(hours: readonly WorkingDay[], index: number, change: Partial<WorkingDay>): WorkingDay[] {
  return hours.map((d, i) => (i === index ? { ...d, ...change } : { ...d }));
}

export function sameHours(a: readonly WorkingDay[], b: readonly WorkingDay[]): boolean {
  return a.length === b.length && a.every((d, i) => d.open === b[i]!.open && d.from === b[i]!.from && d.to === b[i]!.to);
}

export const cloneHours = (hours: readonly WorkingDay[]): WorkingHours => hours.map((d) => ({ ...d }));

export const cloneShifts = (shifts: readonly WorkingShifts[number][]): WorkingShifts =>
  shifts.map((s) => ({ name: s.name, days: cloneHours(s.days) }));

export function sameShifts(a: readonly WorkingShifts[number][], b: readonly WorkingShifts[number][]): boolean {
  return a.length === b.length && a.every((s, i) => s.name === b[i]!.name && sameHours(s.days, b[i]!.days));
}

/** The first free «شیفت N», so a deleted shift's name can come back. */
export function nextShiftName(shifts: readonly { name: string }[], label: (n: number) => string): string {
  const taken = new Set(shifts.map((s) => s.name.trim()));
  for (let n = shifts.length + 1; ; n++) if (!taken.has(label(n))) return label(n);
}

/** A shift added after the others starts as the default week; the admin then edits it. */
export function addShift(shifts: readonly WorkingShifts[number][], name: string): WorkingShifts {
  if (shifts.length >= WORKING_SHIFTS_MAX) return cloneShifts(shifts);
  return [...cloneShifts(shifts), { name, days: cloneHours(DEFAULT_WORKING_HOURS) }];
}

export const removeShift = (shifts: readonly WorkingShifts[number][], index: number): WorkingShifts =>
  cloneShifts(shifts.filter((_, i) => i !== index));

/** The name problem of each shift: empty, or the same name as an earlier shift. */
export function nameProblems(shifts: readonly { name: string }[]): ('empty' | 'duplicate' | null)[] {
  const seen = new Set<string>();
  return shifts.map((s) => {
    const n = s.name.trim();
    if (!n) return 'empty';
    if (seen.has(n)) return 'duplicate';
    seen.add(n);
    return null;
  });
}
